import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../Include/Header";
import Sidebar from "../Include/Sidebar";
import Footer from "../Include/Footer";
import Loader from "../Include/Loader";
import { Pagination } from "../Table";
import { getStoreQuotation } from "../../../Store/Store/StoreQuotation/getStoreQuotation";
import { STATUS_CONFIG } from "../../../helper/storeQuotation";
import axios from "axios";
import toast from "react-hot-toast";
import { V_URL } from "../../../BaseUrl";
import PageHeader from "../Components/PageHeader/PageHeader";
import useDebounce from "../../../hooks/useDebounce";
import { getAdminItem } from "../../../Store/Store/Item/AdminItem";
import { getProject } from "../../../Store/Store/Project/Project";
import { getCategory } from "../../../Store/Store/StoreMaster/Category/Category";

const StoreQuotationList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [limit, setlimit] = useState(10);

    const debouncedSearch = useDebounce(search, 500);

    const [filter, setFilter] = useState({
        startDate: null,
        endDate: null,
        item: null,
        category: null,
        project: null,
    });

    useEffect(() => {
        dispatch(getAdminItem({ is_main: true }));
        dispatch(getProject());
        dispatch(getCategory());
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [debouncedSearch, filter, currentPage, limit]);

    const fetchData = () => {
        const payload = {
            search: debouncedSearch,
            start_date: filter.startDate,
            end_date: filter.endDate,
            item_id: filter.item,
            item_category_id: filter.category,
            project_id: filter.project,
            firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
            year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
        };

        dispatch(getStoreQuotation(payload));
    }

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setCurrentPage(1);
    };

    const { user, loading, error } = useSelector(
        (state) => state?.getStoreQuotation
    );
    const itemData = useSelector((state) => state?.getAdminItem?.user?.data);
    const projectData = useSelector((state) => state?.getProject?.user?.data);
    const categoryData = useSelector((state) => state?.getCategory?.user?.data);

    const commentsData = useMemo(() => {
        return user?.data?.data || [];
    }, [user?.data]);

    useEffect(() => {
        if (user?.data?.pagination) {
            setTotalItems(user?.data?.pagination?.total);
        }
    }, [user?.data?.pagination]);

    const handleDelete = (id, title) => {
        Swal.fire({
            title: `Are you sure want to delete ${title}?`,
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                const res = await axios.delete(`${V_URL}/prosafe/quotation/${id}/delete`, {
                    updated_by: localStorage.getItem("PAY_USER_ID")
                });
                if (res?.data?.success) {
                    toast.success(res?.data?.message || "Quotation deleted successfully");
                    dispatch(getStoreQuotation({
                        search: debouncedSearch,
                        limit,
                        page: currentPage
                    }));
                } else {
                    toast.error(res?.data?.message || "Quotation deleted failed");
                }
            }
        });
    };

    const handleRefresh = () => {
        setFilter({
            startDate: null,
            endDate: null,
            item: null,
            category: null,
            project: null,
        })
    };

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const handleOpen = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleDownloadExcel = async () => {
        try {
            const payload = {
                search,
                start_date: filter.startDate,
                end_date: filter.endDate,
                item_id: filter.item,
                item_category_id: filter.category,
                project_id: filter.project,
                firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
                year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
            };

            const res = await axios.post(
                `${V_URL}/prosafe/quotation/download-quotation`,
                payload,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                    },
                }
            );

            if (res?.data) {
                window.open(res?.data?.data?.url);
                toast.success(res?.data?.message || "Excel downloaded successfully");
            } else {
                toast.error("Failed to download");
            }
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || "Download failed");
        }
    };

    const breadCrumbs = [
        { label: "Dashboard", link: "/" },
        { label: "Store Quotation" },
    ];

    const filterConfig = [
        {
            key: "item",
            label: "Item",
            type: "dropdown",
            value: filter.item,
            placeholder: "Select Item",
            searchable: true,
            options:
                itemData?.map((item) => ({ label: item.name, value: item._id })) || [],
        },
        {
            key: "category",
            label: "Item Category",
            type: "dropdown",
            value: filter.category,
            placeholder: "Select Item Category",
            searchable: true,
            options:
                categoryData?.map((category) => ({
                    label: category.name,
                    value: category._id,
                })) || [],
        },
        {
            key: "project",
            label: "Project",
            type: "dropdown",
            value: filter.project,
            placeholder: "Select Project",
            searchable: true,
            options:
                projectData?.map((project) => ({
                    label: `${project.name} (${project.project_no})`,
                    value: project._id,
                })) || [],
        },
        {
            key: "startDate",
            label: "Start Date",
            type: "date",
            value: filter.startDate,
            placeholder: "Select Start Date",
        },
        {
            key: "endDate",
            label: "End Date",
            type: "date",
            value: filter.endDate,
            placeholder: "Select End Date",
        },
    ];

    const handleDownload = async (id) => {
        try {
            const res = await axios.get(
                `${V_URL}/prosafe/quotation/download-finalize-quotation/${id}`,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                    },
                },
            );
            if (res?.data?.success === true) {
                window.open(res?.data?.data?.fileUrl);
                toast.success(res?.data?.message);
            } else {
                toast.error(res?.data?.message || "Something went wrong");
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handlePdfDownload = async (id) => {
        try {
            const res = await axios.get(
                `${V_URL}/prosafe/quotation/download-quotation/${id}`,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                    },
                },
            );
            if (res?.data?.success === true) {
                window.open(res?.data?.data?.fileUrl);
                toast.success(res?.data?.message);
            } else {
                toast.error(res?.data?.message || "Something went wrong");
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleClone = async (id, voucher_no) => {
        const result = await Swal.fire({
            title: `${voucher_no}`,
            text: `Are you sure you want to duplicate this quotation? This will copy all the details of this quotation to a new quotation.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, duplicate it!",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            const res = await axios.post(
                `${V_URL}/prosafe/quotation/${id}/duplicate-quotation`,
                {
                    created_by: localStorage.getItem("PAY_USER_ID"),
                },
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                    },
                }
            );

            if (res?.data?.success === true) {
                toast.success(res?.data?.message);
                fetchData()
            } else {
                toast.error(res?.data?.message || "Something went wrong");
            }
        } catch (err) {
            console.log(err);
            toast.error("Error cloning quotation");
        }
    };

    return (
        <>
            <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
                <Header handleOpen={handleOpen} />
                <Sidebar />
                <div className="page-wrapper">
                    <div className="content">

                        <div className="row">
                            <div className="col-sm-12">
                                <div className="card card-table show-entire">
                                    <div className="card-body">

                                        <PageHeader
                                            title="Store Quotation"
                                            breadcrumbs={breadCrumbs}
                                            onSearch={(value) => {
                                                setSearch(value);
                                                setCurrentPage(1);
                                            }}
                                            // filter={filter}
                                            // onFilterChange={handleFilterChange}
                                            // filterConfig={filterConfig}
                                            // onDownload={handleDownloadExcel}
                                            addButtonLink="/main-store/user/manage-store-quotation"
                                            onRefresh={handleRefresh}
                                            limit={limit}
                                            onLimitChange={(val) => setlimit(val)}
                                        />

                                        {!loading ? (
                                            <div className="table-responsive">
                                                <table className="table border-0 custom-table comman-table  mb-0 datatable">
                                                    <thead>
                                                        <tr>
                                                            <th>Sr.</th>
                                                            <th>V.No</th>
                                                            {/* <th>Vender Code</th> */}
                                                            <th>Total Qty</th>
                                                            <th>Total Amount (₹)</th>
                                                            <th>Status</th>
                                                            <th className="text-end">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {commentsData?.map((elem, i) => (
                                                            <tr key={elem?._id}>
                                                                <td>{(currentPage - 1) * limit + i + 1}</td>
                                                                <td>{elem?.voucher_no}</td>
                                                                {/* <td>{elem?.estimate_details?.vendor_code || "-"}</td> */}
                                                                <td>{Number(elem?.total_quantity || 0).toFixed(2)}</td>
                                                                <td>{Number(elem?.total_payable || 0).toFixed(2)}</td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <span className="rounded-2 px-3 py-1" style={{
                                                                            backgroundColor: STATUS_CONFIG[elem?.status]?.bg || '#f1f5f9',
                                                                            color: STATUS_CONFIG[elem?.status]?.color || '#64748b',
                                                                            fontSize: "12px",
                                                                            fontWeight: "500",
                                                                        }}>
                                                                            {STATUS_CONFIG[elem?.status]?.label || (elem?.status?.charAt(0).toUpperCase() + elem?.status?.slice(1)) || "N/A"}
                                                                        </span>
                                                                        {/* {elem?.is_finalized && (
                                                                            <span className="bg-success-light px-3 py-1 rounded-4 fw-bold" style={{ fontSize: "12px", color: '#166534', backgroundColor: '#d1fce0' }}>
                                                                                ✓ Finalized
                                                                            </span>
                                                                        )} */}
                                                                    </div>
                                                                </td>

                                                                <td className="text-end">
                                                                    <div className="dropdown dropdown-action">
                                                                        <a
                                                                            href="#"
                                                                            className="action-icon dropdown-toggle"
                                                                            data-bs-toggle="dropdown"
                                                                            aria-expanded="false"
                                                                        >
                                                                            <i className="fa fa-ellipsis-v"></i>
                                                                        </a>
                                                                        <div className="dropdown-menu dropdown-menu-end">
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-item"
                                                                                onClick={() =>
                                                                                    navigate("/main-store/user/manage-store-quotation", {
                                                                                        state: elem,
                                                                                    })
                                                                                }
                                                                            >
                                                                                <i className="fa-solid fa-pen-to-square m-r-5"></i>
                                                                                Edit
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-item"
                                                                                onClick={() => handleDelete(elem._id, elem?.voucher_no)}
                                                                            >
                                                                                <i className="fa-solid fa-trash m-r-5"></i>
                                                                                Delete
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-item"
                                                                                onClick={() => handleClone(elem._id, elem?.voucher_no)}
                                                                            >
                                                                                <i className="fa-solid fa-copy m-r-5"></i>
                                                                                Duplicate Quotation
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-item"
                                                                                onClick={() => handlePdfDownload(elem._id)}
                                                                            >
                                                                                <i className="fa-solid fa-file-pdf m-r-5"></i>
                                                                                Download pdf
                                                                            </button>
                                                                            {(elem?.status === "finalized" || elem?.status === "ordered") && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleDownload(elem._id)}
                                                                                >
                                                                                    <i className="fa-solid fa-download m-r-5"></i>
                                                                                    Download Finalize
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}

                                                        {commentsData?.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="999">
                                                                    <div className="no-table-data">
                                                                        No Data Found!
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ) : null}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <Loader />
                                        )}

                                        <div className="row align-center mt-3 mb-2">
                                            <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                                <div
                                                    className="dataTables_info"
                                                    id="DataTables_Table_0_info"
                                                    role="status"
                                                    aria-live="polite"
                                                >
                                                    Showing {Math.min(limit, totalItems)} from {totalItems} data
                                                </div>
                                            </div>
                                            <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6 ">
                                                <div
                                                    className="dataTables_paginate paging_simple_numbers"
                                                    id="DataTables_Table_0_paginate"
                                                >
                                                    <Pagination
                                                        total={totalItems}
                                                        itemsPerPage={limit}
                                                        currentPage={currentPage}
                                                        onPageChange={(page) => setCurrentPage(page)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </>
    );
};

export default StoreQuotationList;
